/**
 * SupplierNegotiation.jsx - 60-second real-time negotiation minigame
 *
 * Timer does NOT pause with game - creates real pressure
 * Max 3 counter-offers before final round
 * Mood system determines supplier flexibility
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { Button } from "@/components/ui/button";
import {
  FUEL_SUPPLIERS,
  MOOD_LEVELS,
  getMoodFromScore,
  startSupplierNegotiation,
  submitNegotiationOffer,
  acceptSupplierOffer,
  walkAwayFromNegotiation,
  completeNegotiation,
  formatGallons,
} from "../../systems/FuelSystem.js";

const NEGOTIATION_TIME = 60; // seconds

// Shared overlay styles
const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1100,
};

const panelStyle = {
  backgroundColor: "#0c0c0c",
  border: "1px solid #1a1a1a",
  borderRadius: "12px",
  width: "95%",
  maxWidth: "700px",
  maxHeight: "90vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

function MoodIndicator({ mood, moodScore }) {
  const moodConfig = MOOD_LEVELS[mood];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          fontSize: "32px",
          lineHeight: 1,
        }}
      >
        {moodConfig.emoji}
      </div>
      <div>
        <div style={{ color: "#525252", fontSize: "10px" }}>SUPPLIER MOOD</div>
        <div style={{ color: moodConfig.color, fontSize: "16px", fontWeight: "bold" }}>
          {mood}
        </div>
      </div>
      {/* Mood bar */}
      <div
        style={{
          flex: 1,
          height: "8px",
          backgroundColor: "#1a1a1a",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${moodScore}%`,
            height: "100%",
            backgroundColor: moodConfig.color,
            transition: "width 0.3s, background-color 0.3s",
          }}
        />
      </div>
    </div>
  );
}

function Timer({ secondsRemaining, onTimeout }) {
  const isUrgent = secondsRemaining <= 15;
  const isCritical = secondsRemaining <= 5;

  useEffect(() => {
    if (secondsRemaining <= 0) {
      onTimeout();
    }
  }, [secondsRemaining, onTimeout]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        backgroundColor: isCritical ? "#3d0a0a" : isUrgent ? "#3d1a0a" : "#1a1a1a",
        borderRadius: "8px",
        border: `1px solid ${isCritical ? "#ef4444" : isUrgent ? "#f97316" : "#333"}`,
      }}
    >
      <div
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor: isCritical ? "#ef4444" : isUrgent ? "#f97316" : "#22c55e",
          animation: isCritical ? "pulse 0.5s infinite" : isUrgent ? "pulse 1s infinite" : "none",
        }}
      />
      <div
        style={{
          color: isCritical ? "#ef4444" : isUrgent ? "#f97316" : "#e5e5e5",
          fontSize: "20px",
          fontFamily: "monospace",
          fontWeight: "bold",
          minWidth: "40px",
        }}
      >
        {secondsRemaining}s
      </div>
    </div>
  );
}

function OfferBuilder({ supplier, currentOffer, supplierOffer, onSubmit, countersRemaining }) {
  const [rate, setRate] = useState(currentOffer?.rate || supplierOffer.rate * 0.95);
  const [allocation, setAllocation] = useState(currentOffer?.allocationGal || supplierOffer.allocationGal);
  const [duration, setDuration] = useState(currentOffer?.durationMonths || supplierOffer.durationMonths);

  const discount = ((supplierOffer.rate - rate) / supplierOffer.rate) * 100;
  const isAggressive = discount > 10;
  const isVeryAggressive = discount > 15;

  const handleSubmit = () => {
    onSubmit({ rate, allocationGal: allocation, durationMonths: duration });
  };

  return (
    <div style={{ padding: "20px", backgroundColor: "#0a0a0a", borderRadius: "8px" }}>
      <div style={{ color: "#737373", fontSize: "12px", fontWeight: "600", marginBottom: "16px" }}>
        YOUR COUNTER-OFFER ({countersRemaining} remaining)
      </div>

      {/* Rate */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <span style={{ color: "#525252", fontSize: "11px" }}>PROPOSED RATE</span>
          <span
            style={{
              color: isVeryAggressive ? "#ef4444" : isAggressive ? "#eab308" : "#22c55e",
              fontSize: "11px",
            }}
          >
            {discount > 0 ? `-${discount.toFixed(1)}%` : `+${Math.abs(discount).toFixed(1)}%`}
            {isVeryAggressive && " (RISKY)"}
          </span>
        </div>
        <input
          type="range"
          min={supplier.baseRate * 0.75}
          max={supplierOffer.rate * 1.05}
          step={0.01}
          value={rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
          style={{ width: "100%", accentColor: "#60ffd0" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
          <span style={{ color: "#737373", fontSize: "10px" }}>${(supplier.baseRate * 0.75).toFixed(2)}</span>
          <span style={{ color: "#60ffd0", fontSize: "18px", fontFamily: "monospace", fontWeight: "bold" }}>
            ${rate.toFixed(2)}/gal
          </span>
          <span style={{ color: "#737373", fontSize: "10px" }}>${supplierOffer.rate.toFixed(2)}</span>
        </div>
      </div>

      {/* Allocation */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ color: "#525252", fontSize: "11px", marginBottom: "6px" }}>
          MONTHLY ALLOCATION
        </div>
        <input
          type="range"
          min={supplier.minAllocationGal}
          max={supplier.maxAllocationGal}
          step={10000}
          value={allocation}
          onChange={(e) => setAllocation(parseInt(e.target.value))}
          style={{ width: "100%", accentColor: "#60ffd0" }}
        />
        <div style={{ textAlign: "center", marginTop: "4px" }}>
          <span style={{ color: "#e5e5e5", fontSize: "16px" }}>
            {formatGallons(allocation)} gallons/month
          </span>
        </div>
      </div>

      {/* Duration */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ color: "#525252", fontSize: "11px", marginBottom: "6px" }}>
          CONTRACT DURATION
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {[supplier.minContractMonths, 6, 12, supplier.maxContractMonths]
            .filter((v, i, a) => a.indexOf(v) === i && v >= supplier.minContractMonths && v <= supplier.maxContractMonths)
            .slice(0, 4)
            .map((months) => (
              <button
                key={months}
                onClick={() => setDuration(months)}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundColor: duration === months ? "#1a3d1a" : "#1a1a1a",
                  border: `1px solid ${duration === months ? "#22c55e" : "#333"}`,
                  borderRadius: "6px",
                  color: duration === months ? "#4ade80" : "#a1a1a1",
                  cursor: "pointer",
                }}
              >
                {months}mo
              </button>
            ))}
        </div>
      </div>

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        style={{
          width: "100%",
          padding: "14px",
          backgroundColor: isVeryAggressive ? "#3d1a0a" : "#1a2d4d",
          borderColor: isVeryAggressive ? "#f97316" : "#3b82f6",
          color: isVeryAggressive ? "#fb923c" : "#60a5fa",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        SUBMIT COUNTER-OFFER
      </Button>
    </div>
  );
}

function SupplierOfferDisplay({ supplier, offer, mood }) {
  const moodConfig = MOOD_LEVELS[mood];

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#111",
        borderRadius: "8px",
        border: `2px solid ${moodConfig.color}40`,
      }}
    >
      <div style={{ color: "#737373", fontSize: "12px", fontWeight: "600", marginBottom: "12px" }}>
        {supplier.name.toUpperCase()}'S OFFER
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
        <div>
          <div style={{ color: "#525252", fontSize: "10px" }}>RATE</div>
          <div style={{ color: "#60ffd0", fontSize: "24px", fontFamily: "monospace", fontWeight: "bold" }}>
            ${offer.rate.toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ color: "#525252", fontSize: "10px" }}>ALLOCATION</div>
          <div style={{ color: "#e5e5e5", fontSize: "20px", fontWeight: "bold" }}>
            {formatGallons(offer.allocationGal)}
          </div>
        </div>
        <div>
          <div style={{ color: "#525252", fontSize: "10px" }}>DURATION</div>
          <div style={{ color: "#e5e5e5", fontSize: "20px", fontWeight: "bold" }}>
            {offer.durationMonths}mo
          </div>
        </div>
      </div>
    </div>
  );
}

function NegotiationResult({ result, supplier, finalRate, finalAllocation, finalDuration, onComplete }) {
  const isSuccess = result === "DEAL";

  return (
    <div style={{ textAlign: "center", padding: "40px" }}>
      {/* Result icon */}
      <div style={{ fontSize: "64px", marginBottom: "16px" }}>
        {isSuccess ? "🤝" : result === "WALKED_AWAY" ? "😠" : "🚶"}
      </div>

      {/* Result text */}
      <div
        style={{
          color: isSuccess ? "#22c55e" : "#ef4444",
          fontSize: "28px",
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        {isSuccess ? "DEAL CLOSED!" : result === "WALKED_AWAY" ? "SUPPLIER WALKED AWAY" : "NO DEAL"}
      </div>

      <div style={{ color: "#737373", fontSize: "14px", marginBottom: "32px" }}>
        {isSuccess
          ? `You've secured a contract with ${supplier.name}`
          : result === "WALKED_AWAY"
          ? `${supplier.name} was insulted by your offer. Try again in 30 days.`
          : "You walked away from the negotiation."}
      </div>

      {/* Contract details if successful */}
      {isSuccess && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px",
            padding: "24px",
            backgroundColor: "#0a1a0a",
            borderRadius: "8px",
            border: "1px solid #22c55e40",
            marginBottom: "32px",
          }}
        >
          <div>
            <div style={{ color: "#525252", fontSize: "11px" }}>RATE</div>
            <div style={{ color: "#60ffd0", fontSize: "24px", fontFamily: "monospace", fontWeight: "bold" }}>
              ${finalRate?.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ color: "#525252", fontSize: "11px" }}>ALLOCATION</div>
            <div style={{ color: "#e5e5e5", fontSize: "20px", fontWeight: "bold" }}>
              {formatGallons(finalAllocation || 0)}
            </div>
          </div>
          <div>
            <div style={{ color: "#525252", fontSize: "11px" }}>DURATION</div>
            <div style={{ color: "#e5e5e5", fontSize: "20px", fontWeight: "bold" }}>
              {finalDuration}mo
            </div>
          </div>
        </div>
      )}

      <Button
        onClick={onComplete}
        style={{
          padding: "14px 40px",
          backgroundColor: isSuccess ? "#1a3d1a" : "#1a1a1a",
          borderColor: isSuccess ? "#22c55e" : "#333",
          color: isSuccess ? "#4ade80" : "#a1a1a1",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {isSuccess ? "CLOSE" : "RETURN"}
      </Button>
    </div>
  );
}

export function SupplierNegotiation({ supplierId, onComplete, onCancel }) {
  const { worldState, updateWorldState } = useWorldState();
  const [secondsRemaining, setSecondsRemaining] = useState(NEGOTIATION_TIME);
  const [negotiationState, setNegotiationState] = useState(null);
  const [phase, setPhase] = useState("loading");

  const supplier = FUEL_SUPPLIERS[supplierId];
  const fuelState = worldState?.fuel || {};
  const reputationTier = worldState?.tier || 1;
  const macroState = worldState?.macro || {};

  // Initialize negotiation
  useEffect(() => {
    const result = startSupplierNegotiation(fuelState, supplierId, reputationTier, macroState);
    if (result.success) {
      setNegotiationState(result.newFuelState.activeNegotiation);
      setPhase("negotiating");
    } else {
      onCancel();
    }
  }, []);

  // Timer countdown
  useEffect(() => {
    if (phase !== "negotiating") return;

    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  // Handle timeout
  const handleTimeout = useCallback(() => {
    if (phase === "negotiating") {
      setPhase("result");
      setNegotiationState((prev) => ({
        ...prev,
        result: "TIMEOUT",
        phase: "result",
      }));
    }
  }, [phase]);

  // Handle player counter-offer
  const handleCounterOffer = useCallback(
    (offer) => {
      const currentFuelState = { ...fuelState, activeNegotiation: negotiationState };
      const result = submitNegotiationOffer(currentFuelState, offer);

      if (result.success) {
        const newNeg = result.newFuelState.activeNegotiation;
        setNegotiationState(newNeg);

        if (result.result === "ACCEPTED" || result.result === "WALKED_AWAY") {
          setPhase("result");
        }
      }
    },
    [fuelState, negotiationState]
  );

  // Handle accept supplier offer
  const handleAccept = useCallback(() => {
    const currentFuelState = { ...fuelState, activeNegotiation: negotiationState };
    const result = acceptSupplierOffer(currentFuelState);

    if (result.success) {
      setNegotiationState(result.newFuelState.activeNegotiation);
      setPhase("result");
    }
  }, [fuelState, negotiationState]);

  // Handle walk away
  const handleWalkAway = useCallback(() => {
    const currentFuelState = { ...fuelState, activeNegotiation: negotiationState };
    const result = walkAwayFromNegotiation(currentFuelState);

    if (result.success) {
      setNegotiationState(result.newFuelState.activeNegotiation);
      setPhase("result");
    }
  }, [fuelState, negotiationState]);

  // Handle completion
  const handleComplete = useCallback(() => {
    if (!negotiationState) {
      onComplete(null);
      return;
    }

    const currentFuelState = { ...fuelState, activeNegotiation: negotiationState };
    const result = completeNegotiation(currentFuelState, worldState.gameTime || new Date().toISOString());

    if (result.success) {
      updateWorldState((prev) => ({
        ...prev,
        fuel: result.newFuelState,
      }));
      onComplete(result.contract);
    } else {
      onComplete(null);
    }
  }, [fuelState, negotiationState, worldState, updateWorldState, onComplete]);

  if (!supplier || !negotiationState) {
    return (
      <div style={overlayStyle}>
        <div style={{ color: "#525252" }}>Loading...</div>
      </div>
    );
  }

  const countersRemaining = negotiationState.counterLimit - negotiationState.countersUsed;
  const isFinalRound = negotiationState.phase === "final";

  return (
    <div style={overlayStyle} onClick={(e) => e.stopPropagation()}>
      <div style={panelStyle}>
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #1a1a1a",
            backgroundColor: "#0a0a0a",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ color: "#60ffd0", fontSize: "18px", fontWeight: "bold", letterSpacing: "1px" }}>
              NEGOTIATION
            </div>
            <div style={{ color: "#737373", fontSize: "12px" }}>{supplier.name}</div>
          </div>
          {phase === "negotiating" && <Timer secondsRemaining={secondsRemaining} onTimeout={handleTimeout} />}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {phase === "negotiating" && (
            <>
              {/* Mood indicator */}
              <div style={{ marginBottom: "20px" }}>
                <MoodIndicator mood={negotiationState.mood} moodScore={negotiationState.moodScore} />
              </div>

              {/* Final round warning */}
              {isFinalRound && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#3d1a0a",
                    border: "1px solid #f97316",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ color: "#fb923c", fontSize: "14px", fontWeight: "bold" }}>
                    FINAL ROUND - Accept or walk away
                  </div>
                </div>
              )}

              {/* Supplier's current offer */}
              <div style={{ marginBottom: "20px" }}>
                <SupplierOfferDisplay
                  supplier={supplier}
                  offer={negotiationState.supplierLastOffer}
                  mood={negotiationState.mood}
                />
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
                <Button
                  onClick={handleAccept}
                  style={{
                    flex: 1,
                    padding: "14px",
                    backgroundColor: "#1a3d1a",
                    borderColor: "#22c55e",
                    color: "#4ade80",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  ACCEPT OFFER
                </Button>
                <Button
                  onClick={handleWalkAway}
                  style={{
                    flex: 1,
                    padding: "14px",
                    backgroundColor: "#1a1a1a",
                    borderColor: "#525252",
                    color: "#a1a1a1",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  WALK AWAY
                </Button>
              </div>

              {/* Counter-offer builder (if not final round) */}
              {!isFinalRound && countersRemaining > 0 && (
                <OfferBuilder
                  supplier={supplier}
                  currentOffer={negotiationState.currentOffer}
                  supplierOffer={negotiationState.supplierLastOffer}
                  onSubmit={handleCounterOffer}
                  countersRemaining={countersRemaining}
                />
              )}
            </>
          )}

          {phase === "result" && (
            <NegotiationResult
              result={negotiationState.result || "TIMEOUT"}
              supplier={supplier}
              finalRate={negotiationState.finalRate}
              finalAllocation={negotiationState.finalAllocation}
              finalDuration={negotiationState.finalDuration}
              onComplete={handleComplete}
            />
          )}
        </div>
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default SupplierNegotiation;
